/**
 * The camera, pointed at a label.
 *
 * ## Why a library and not `BarcodeDetector`
 *
 * The native API is the obvious choice and only exists in Chromium. Safari does not
 * have it, which on a building site means the iPhone half of the company cannot scan —
 * and that is the half that would quietly stop using the feature rather than report it.
 * ZXing reads both symbologies everywhere, and is loaded only when this component
 * mounts, so the 200kB never reaches anyone who does not open the scanner.
 *
 * ## Why the failures are spelled out
 *
 * Four different things stop a camera working and they need four different answers:
 * an insecure origin (deploy it), a denied permission (change a browser setting), no
 * camera at all (type the code), and a camera busy in another app (close it). A single
 * "could not start camera" sends somebody to the wrong one of those and wastes an
 * afternoon. There is always a text box as well, because a scratched label is a normal
 * Tuesday.
 */

import { useEffect, useRef, useState } from 'react';

import { useI18n } from '@/i18n';

type Problem = 'insecure' | 'denied' | 'none' | 'busy' | null;

/** `getUserMedia` exists only in a secure context, and localhost counts as one. */
function secureEnough(): boolean {
  return window.isSecureContext;
}

export function CodeScanner({ onCode }: { onCode: (code: string) => void }) {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState<Problem>(null);

  /*
   * The latest handler, without restarting the camera.
   *
   * `onCode` is a new function on every render of the parent. In the effect's deps it
   * would tear the video stream down and build it up again on each keystroke elsewhere
   * on the page — a visible flicker, and a permission prompt on some browsers.
   */
  const handler = useRef(onCode);
  useEffect(() => {
    handler.current = onCode;
  }, [onCode]);

  useEffect(() => {
    if (!running) return;

    if (!secureEnough()) {
      setProblem('insecure');
      setRunning(false);
      return;
    }

    let stop: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const reader = new BrowserMultiFormatReader();

        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current ?? undefined,
          (result) => {
            if (!result || cancelled) return;
            const text = result.getText().trim();
            if (text) handler.current(text);
          },
        );

        if (cancelled) controls.stop();
        else stop = () => controls.stop();
      } catch (error) {
        if (cancelled) return;
        const name = (error as { name?: string })?.name;
        setProblem(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'denied'
            : name === 'NotFoundError' || name === 'OverconstrainedError'
              ? 'none'
              : 'busy',
        );
        setRunning(false);
      }
    })();

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [running]);

  // The camera light must go out when the page does. Without this, navigating away
  // inside the SPA leaves the phone's torch indicator on, which people notice.
  useEffect(() => () => setRunning(false), []);

  const message =
    problem === 'insecure'
      ? { title: t('inventory.scanInsecure'), body: t('inventory.scanInsecureHint') }
      : problem === 'denied'
        ? { title: t('inventory.scanDenied'), body: t('inventory.scanDeniedHint') }
        : problem
          ? { title: t('inventory.scanNoCamera'), body: t('inventory.scanNoCameraHint') }
          : null;

  return (
    <div className="space-y-3">
      <div className="relative rounded overflow-hidden bg-black aspect-[4/3]">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          muted
          playsInline
          aria-label={t('inventory.scanTitle')}
        />

        {!running ? (
          <div className="absolute inset-0 grid place-items-center bg-surface-alt">
            <button type="button" className="btn-primary" onClick={() => setRunning(true)}>
              {t('inventory.scanStart')}
            </button>
          </div>
        ) : (
          <>
            {/* A frame to aim with. Nothing detects "inside the box" — ZXing reads the
                whole frame — but a target is how somebody knows how close to hold it. */}
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="w-3/5 aspect-square border-2 border-white/80 rounded-lg" />
            </div>
            <button
              type="button"
              className="btn-secondary absolute bottom-3 end-3"
              onClick={() => setRunning(false)}
            >
              {t('inventory.scanStop')}
            </button>
          </>
        )}
      </div>

      {message ? (
        <div className="p-3 rounded border border-[var(--color-warning)] bg-warning-soft">
          <p className="text-sm font-semibold">{message.title}</p>
          <p className="text-sm text-muted mt-0.5 leading-relaxed">{message.body}</p>
        </div>
      ) : (
        <p className="text-sm text-muted">{t('inventory.scanHint')}</p>
      )}
    </div>
  );
}
